/**
 * Факты торговли робота и «проверьте сами» — общие тексты витрины на главной
 * и калькуляторов на страницах торговли (HistoryFacts, CheckYourself).
 */
export interface FactsTranslations {
    factsTitle: string
    period: string
    earned: string
    earnedHint: string
    deals: string
    volumeGlobal: string
    volumeCrypto: string
    perDayTitle: string
    perLot: string
    perContract: string
    perTrade: string
    byDays: string
    dealsShort: string
    contractsShort: string
    sharpe: string
    sharpeHint: string
    maxDrawdown: string
    drawdownHint: string
    sortino: string
    sortinoHint: string
    metricsTitle: string
    equityDrawdown: string
    equityHint: string
    viewOwn: string
    viewRobot: string
    ownEmpty: string
    ownInstrument: string
    referenceNote: string
    metricsBasis: string
    metricsDeals: string
    metricsFew: string
    calmar: string
    calmarHint: string
    volatility: string
    periodReturn: string
    profitFactor: string
    pfHint: string
    winRate: string
    winDays: string
    bestDay: string
    worstDay: string
    noLosses: string
    checkTitle: string
    checkHint: string
    deposit: string
    lot: string
    take: string
    contractsDay: string
    tradesDay: string
    fact: string
    reset: string
    takeNote: string
    pts: string
    takePrice: string
    robotDay: string
    yourShare: string
    perDay: string
    perMonth: string
    perYear: string
    yearPct: string
}

const ru: FactsTranslations = {
    factsTitle: 'Как робот торговал',
    period: '{from} — {to} · {days} дн. торговли · счетов: {accounts}',
    earned: 'Заработано',
    earnedHint: 'чистыми после комиссий биржи, до деления 50/50',
    deals: 'Сделок',
    volumeGlobal: 'Контрактов',
    volumeCrypto: 'Объём, {coin}',
    perDayTitle: 'В среднем за день',
    perLot: 'на 1 лот входа',
    perContract: 'на контракт',
    perTrade: 'на сделку',
    byDays: 'По дням',
    dealsShort: 'сд.',
    contractsShort: 'контр.',
    sharpe: 'Шарп',
    sharpeHint: 'выше 1 — хорошо, выше 3 — отлично',
    maxDrawdown: 'Худшее снижение',
    drawdownHint: 'на сколько счёт временно проседал',
    sortino: 'Сортино',
    sortinoHint: 'как Шарп, но считает только снижения',
    metricsTitle: 'Показатели риска и доходности',
    equityDrawdown: 'Просадка открытой позиции',
    equityHint: 'по снимкам капитала (баланс + плавающий результат), дней: {days}; в остальных показателях — только закрытые сделки',
    viewOwn: 'Мой счёт',
    viewRobot: 'Робот в целом',
    ownEmpty: 'По вашему счёту сделок по этому инструменту пока нет',
    ownInstrument: 'Ваш счёт',
    referenceNote:
        'По {symbol} сделок ещё нет. Для примера — факты по {ref}; расчёт справа — по тейку {symbol}, сделок в день — как у {ref}.',
    metricsBasis: 'по {days} торг. дн. · {from} — {to}',
    metricsDeals: 'по закрытым сделкам',
    metricsFew:
        'Пока меньше 2 торговых дней — показатели появятся со вторым днём.',
    calmar: 'Кальмар',
    calmarHint: 'годовая доходность / макс. просадка',
    volatility: 'Волатильность, год',
    periodReturn: 'Доходность за период',
    profitFactor: 'Профит-фактор',
    pfHint: 'прибыль сделок / убыток сделок',
    winRate: 'Прибыльных сделок',
    winDays: 'Прибыльных дней',
    bestDay: 'Лучший день',
    worstDay: 'Худший день',
    noLosses: 'убыточных не было',
    checkTitle: 'Проверьте сами',
    checkHint: 'Двигайте ползунки. Сначала они стоят на фактах робота.',
    deposit: 'Депозит',
    lot: 'Лот на входе',
    take: 'Тейк-профит, пунктов',
    contractsDay: 'Контрактов в день на 1 лот',
    tradesDay: 'Сделок в день',
    fact: 'факт: {v}',
    reset: 'Вернуть факты',
    takeNote:
        'Факт набран с тейком {hist}; с другим тейком — расчёт: сделок меньше, но каждая больше.',
    pts: 'п.',
    takePrice: 'Тейк-профит, ход цены',
    robotDay: 'Робот в день',
    yourShare: 'Вам 50%',
    perDay: 'в день',
    perMonth: 'в месяц',
    perYear: 'в год',
    yearPct: '{pct}% к депозиту в год',
}

const en: FactsTranslations = {
    factsTitle: 'How the robot traded',
    period: '{from} — {to} · {days} trading days · accounts: {accounts}',
    earned: 'Earned',
    earnedHint: 'net of exchange fees, before the 50/50 split',
    deals: 'Trades',
    volumeGlobal: 'Contracts',
    volumeCrypto: 'Volume, {coin}',
    perDayTitle: 'Average per day',
    perLot: 'per 1 entry lot',
    perContract: 'per contract',
    perTrade: 'per trade',
    byDays: 'By day',
    dealsShort: 'tr.',
    contractsShort: 'ctr.',
    sharpe: 'Sharpe',
    sharpeHint: 'above 1 is good, above 3 is excellent',
    maxDrawdown: 'Worst drawdown',
    drawdownHint: 'how far the account temporarily dipped',
    sortino: 'Sortino',
    sortinoHint: 'like Sharpe, but counts only the dips',
    metricsTitle: 'Risk and return metrics',
    equityDrawdown: 'Open-position drawdown',
    equityHint: 'from equity snapshots (balance + open P&L), days: {days}; other metrics use closed trades only',
    viewOwn: 'My account',
    viewRobot: 'Robot overall',
    ownEmpty: 'Your account has no trades on this instrument yet',
    ownInstrument: 'Your account',
    referenceNote:
        'No trades on {symbol} yet. As an example — facts for {ref}; the calculation on the right uses the {symbol} take, trades per day as for {ref}.',
    metricsBasis: '{days} trading days · {from} — {to}',
    metricsDeals: 'from closed trades',
    metricsFew:
        'Less than 2 trading days so far — metrics appear from the second day.',
    calmar: 'Calmar',
    calmarHint: 'annual return / max drawdown',
    volatility: 'Volatility, annual',
    periodReturn: 'Return for the period',
    profitFactor: 'Profit factor',
    pfHint: 'gross profit / gross loss',
    winRate: 'Winning trades',
    winDays: 'Winning days',
    bestDay: 'Best day',
    worstDay: 'Worst day',
    noLosses: 'no losses',
    checkTitle: 'Check it yourself',
    checkHint: 'Move the sliders. They start at the robot\'s facts.',
    deposit: 'Deposit',
    lot: 'Entry lot',
    take: 'Take profit, points',
    contractsDay: 'Contracts per day per lot',
    tradesDay: 'Trades per day',
    fact: 'fact: {v}',
    reset: 'Back to facts',
    takeNote:
        'The facts were made with a {hist} take. Another take is an estimate: fewer trades, but each one bigger.',
    pts: 'pt',
    takePrice: 'Take profit, price move',
    robotDay: 'Robot per day',
    yourShare: 'Your 50%',
    perDay: 'per day',
    perMonth: 'per month',
    perYear: 'per year',
    yearPct: '{pct}% of the deposit per year',
}

const es: FactsTranslations = {
    factsTitle: 'Cómo operó el robot',
    period: '{from} — {to} · {days} días de operativa · cuentas: {accounts}',
    earned: 'Ganado',
    earnedHint: 'neto de comisiones del exchange, antes del reparto 50/50',
    deals: 'Operaciones',
    volumeGlobal: 'Contratos',
    volumeCrypto: 'Volumen, {coin}',
    perDayTitle: 'Promedio por día',
    perLot: 'por 1 lote de entrada',
    perContract: 'por contrato',
    perTrade: 'por operación',
    byDays: 'Por días',
    dealsShort: 'op.',
    contractsShort: 'contr.',
    sharpe: 'Sharpe',
    sharpeHint: 'más de 1 es bueno, más de 3 es excelente',
    maxDrawdown: 'Peor caída',
    drawdownHint: 'cuánto bajó la cuenta temporalmente',
    sortino: 'Sortino',
    sortinoHint: 'como Sharpe, pero solo cuenta las caídas',
    metricsTitle: 'Métricas de riesgo y rentabilidad',
    equityDrawdown: 'Caída de la posición abierta',
    equityHint: 'según instantáneas del capital (saldo + resultado flotante), días: {days}; el resto, solo operaciones cerradas',
    viewOwn: 'Mi cuenta',
    viewRobot: 'Robot en total',
    ownEmpty: 'Su cuenta aún no tiene operaciones en este instrumento',
    ownInstrument: 'Su cuenta',
    referenceNote:
        'Aún no hay operaciones en {symbol}. Como ejemplo, hechos de {ref}; el cálculo de la derecha usa el take de {symbol} y las operaciones diarias de {ref}.',
    metricsBasis: '{days} días de operativa · {from} — {to}',
    metricsDeals: 'según operaciones cerradas',
    metricsFew:
        'Aún menos de 2 días de operativa: las métricas aparecen desde el segundo día.',
    calmar: 'Calmar',
    calmarHint: 'rentabilidad anual / caída máxima',
    volatility: 'Volatilidad anual',
    periodReturn: 'Rentabilidad del período',
    profitFactor: 'Profit factor',
    pfHint: 'beneficio bruto / pérdida bruta',
    winRate: 'Operaciones ganadoras',
    winDays: 'Días con ganancia',
    bestDay: 'Mejor día',
    worstDay: 'Peor día',
    noLosses: 'sin pérdidas',
    checkTitle: 'Compruébelo usted',
    checkHint: 'Mueva los controles. Empiezan en los hechos del robot.',
    deposit: 'Depósito',
    lot: 'Lote de entrada',
    take: 'Take profit, puntos',
    contractsDay: 'Contratos por día por lote',
    tradesDay: 'Operaciones por día',
    fact: 'hecho: {v}',
    reset: 'Volver a los hechos',
    takeNote:
        'Los hechos se obtuvieron con un take de {hist}. Otro take es una estimación: menos operaciones, pero cada una mayor.',
    pts: 'pt',
    takePrice: 'Take profit, movimiento de precio',
    robotDay: 'El robot por día',
    yourShare: 'Su 50%',
    perDay: 'por día',
    perMonth: 'al mes',
    perYear: 'al año',
    yearPct: '{pct}% del depósito al año',
}

const fr: FactsTranslations = {
    factsTitle: 'Comment le robot a tradé',
    period: '{from} — {to} · {days} jours de trading · comptes : {accounts}',
    earned: 'Gagné',
    earnedHint: 'net des frais de la plateforme, avant le partage 50/50',
    deals: 'Trades',
    volumeGlobal: 'Contrats',
    volumeCrypto: 'Volume, {coin}',
    perDayTitle: 'En moyenne par jour',
    perLot: 'pour 1 lot d\'entrée',
    perContract: 'par contrat',
    perTrade: 'par trade',
    byDays: 'Par jour',
    dealsShort: 'tr.',
    contractsShort: 'contr.',
    sharpe: 'Sharpe',
    sharpeHint: 'au-dessus de 1 c\'est bien, au-dessus de 3 excellent',
    maxDrawdown: 'Pire baisse',
    drawdownHint: 'de combien le compte a baissé temporairement',
    sortino: 'Sortino',
    sortinoHint: 'comme Sharpe, mais ne compte que les baisses',
    metricsTitle: 'Indicateurs de risque et de rendement',
    equityDrawdown: 'Baisse de la position ouverte',
    equityHint: 'd\'après les instantanés du capital (solde + résultat latent), jours : {days} ; les autres indicateurs ne prennent que les trades clôturés',
    viewOwn: 'Mon compte',
    viewRobot: 'Robot au global',
    ownEmpty: 'Votre compte n\'a pas encore de trades sur cet instrument',
    ownInstrument: 'Votre compte',
    referenceNote:
        'Pas encore de trades sur {symbol}. À titre d\'exemple, les faits de {ref} ; le calcul à droite utilise le take de {symbol} et les trades par jour de {ref}.',
    metricsBasis: '{days} jours de trading · {from} — {to}',
    metricsDeals: 'd\'après les trades clôturés',
    metricsFew:
        'Moins de 2 jours de trading pour l\'instant : les indicateurs apparaissent dès le deuxième jour.',
    calmar: 'Calmar',
    calmarHint: 'rendement annuel / baisse max.',
    volatility: 'Volatilité annuelle',
    periodReturn: 'Rendement sur la période',
    profitFactor: 'Profit factor',
    pfHint: 'gains bruts / pertes brutes',
    winRate: 'Trades gagnants',
    winDays: 'Jours gagnants',
    bestDay: 'Meilleur jour',
    worstDay: 'Pire jour',
    noLosses: 'aucune perte',
    checkTitle: 'Vérifiez vous-même',
    checkHint: 'Bougez les curseurs. Ils partent des faits du robot.',
    deposit: 'Dépôt',
    lot: 'Lot d\'entrée',
    take: 'Take profit, points',
    contractsDay: 'Contrats par jour par lot',
    tradesDay: 'Trades par jour',
    fact: 'fait : {v}',
    reset: 'Revenir aux faits',
    takeNote:
        'Les faits ont été obtenus avec un take de {hist}. Un autre take est une estimation : moins de trades, mais chacun plus grand.',
    pts: 'pt',
    takePrice: 'Take profit, mouvement du prix',
    robotDay: 'Le robot par jour',
    yourShare: 'Vos 50 %',
    perDay: 'par jour',
    perMonth: 'par mois',
    perYear: 'par an',
    yearPct: '{pct} % du dépôt par an',
}

const de: FactsTranslations = {
    factsTitle: 'Wie der Roboter gehandelt hat',
    period: '{from} — {to} · {days} Handelstage · Konten: {accounts}',
    earned: 'Verdient',
    earnedHint: 'netto nach Börsengebühren, vor der 50/50-Teilung',
    deals: 'Trades',
    volumeGlobal: 'Kontrakte',
    volumeCrypto: 'Volumen, {coin}',
    perDayTitle: 'Im Schnitt pro Tag',
    perLot: 'pro 1 Einstiegslot',
    perContract: 'pro Kontrakt',
    perTrade: 'pro Trade',
    byDays: 'Nach Tagen',
    dealsShort: 'Tr.',
    contractsShort: 'Kontr.',
    sharpe: 'Sharpe',
    sharpeHint: 'über 1 gut, über 3 ausgezeichnet',
    maxDrawdown: 'Größter Rückgang',
    drawdownHint: 'wie weit das Konto zeitweise fiel',
    sortino: 'Sortino',
    sortinoHint: 'wie Sharpe, zählt aber nur Rückgänge',
    metricsTitle: 'Risiko- und Renditekennzahlen',
    equityDrawdown: 'Rückgang offener Position',
    equityHint: 'aus Kapital-Snapshots (Saldo + offenes Ergebnis), Tage: {days}; übrige Kennzahlen nur aus geschlossenen Trades',
    viewOwn: 'Mein Konto',
    viewRobot: 'Roboter gesamt',
    ownEmpty: 'Ihr Konto hat noch keine Trades in diesem Instrument',
    ownInstrument: 'Ihr Konto',
    referenceNote:
        'Noch keine Trades in {symbol}. Als Beispiel die Fakten von {ref}; die Rechnung rechts nutzt den Take von {symbol}, Trades pro Tag wie bei {ref}.',
    metricsBasis: '{days} Handelstage · {from} — {to}',
    metricsDeals: 'aus geschlossenen Trades',
    metricsFew:
        'Bisher weniger als 2 Handelstage – Kennzahlen ab dem zweiten Tag.',
    calmar: 'Calmar',
    calmarHint: 'Jahresrendite / max. Rückgang',
    volatility: 'Volatilität p. a.',
    periodReturn: 'Rendite im Zeitraum',
    profitFactor: 'Profit-Faktor',
    pfHint: 'Bruttogewinn / Bruttoverlust',
    winRate: 'Gewinntrades',
    winDays: 'Gewinntage',
    bestDay: 'Bester Tag',
    worstDay: 'Schlechtester Tag',
    noLosses: 'keine Verluste',
    checkTitle: 'Selbst prüfen',
    checkHint:
        'Bewegen Sie die Regler. Sie starten bei den Fakten des Roboters.',
    deposit: 'Einlage',
    lot: 'Einstiegslot',
    take: 'Take-Profit, Punkte',
    contractsDay: 'Kontrakte pro Tag je Lot',
    tradesDay: 'Trades pro Tag',
    fact: 'Fakt: {v}',
    reset: 'Zurück zu den Fakten',
    takeNote:
        'Die Fakten entstanden mit {hist} Take. Ein anderer Take ist eine Schätzung: weniger Trades, dafür größere.',
    pts: 'Pkt.',
    takePrice: 'Take-Profit, Kursbewegung',
    robotDay: 'Roboter pro Tag',
    yourShare: 'Ihre 50 %',
    perDay: 'pro Tag',
    perMonth: 'pro Monat',
    perYear: 'pro Jahr',
    yearPct: '{pct} % der Einlage pro Jahr',
}

const it: FactsTranslations = {
    factsTitle: 'Come ha operato il robot',
    period: '{from} — {to} · {days} giorni di trading · conti: {accounts}',
    earned: 'Guadagnato',
    earnedHint:
        'al netto delle commissioni dell\'exchange, prima della divisione 50/50',
    deals: 'Operazioni',
    volumeGlobal: 'Contratti',
    volumeCrypto: 'Volume, {coin}',
    perDayTitle: 'In media al giorno',
    perLot: 'per 1 lotto d\'ingresso',
    perContract: 'per contratto',
    perTrade: 'per operazione',
    byDays: 'Per giorni',
    dealsShort: 'op.',
    contractsShort: 'contr.',
    sharpe: 'Sharpe',
    sharpeHint: 'sopra 1 è buono, sopra 3 è ottimo',
    maxDrawdown: 'Calo peggiore',
    drawdownHint: 'di quanto il conto è sceso temporaneamente',
    sortino: 'Sortino',
    sortinoHint: 'come Sharpe, ma conta solo i cali',
    metricsTitle: 'Metriche di rischio e rendimento',
    equityDrawdown: 'Calo della posizione aperta',
    equityHint: 'dalle istantanee del capitale (saldo + risultato aperto), giorni: {days}; le altre metriche solo dalle operazioni chiuse',
    viewOwn: 'Il mio conto',
    viewRobot: 'Robot nel complesso',
    ownEmpty: 'Il tuo conto non ha ancora operazioni su questo strumento',
    ownInstrument: 'Il tuo conto',
    referenceNote:
        'Ancora nessuna operazione su {symbol}. Come esempio, i fatti di {ref}; il calcolo a destra usa il take di {symbol} e le operazioni al giorno di {ref}.',
    metricsBasis: '{days} giorni di trading · {from} — {to}',
    metricsDeals: 'dalle operazioni chiuse',
    metricsFew:
        'Per ora meno di 2 giorni di trading: le metriche appaiono dal secondo giorno.',
    calmar: 'Calmar',
    calmarHint: 'rendimento annuo / calo massimo',
    volatility: 'Volatilità annua',
    periodReturn: 'Rendimento del periodo',
    profitFactor: 'Profit factor',
    pfHint: 'utile lordo / perdita lorda',
    winRate: 'Operazioni in utile',
    winDays: 'Giorni in utile',
    bestDay: 'Giorno migliore',
    worstDay: 'Giorno peggiore',
    noLosses: 'nessuna perdita',
    checkTitle: 'Verifica tu',
    checkHint: 'Muovi i cursori. Partono dai fatti del robot.',
    deposit: 'Deposito',
    lot: 'Lotto d\'ingresso',
    take: 'Take profit, punti',
    contractsDay: 'Contratti al giorno per lotto',
    tradesDay: 'Operazioni al giorno',
    fact: 'fatto: {v}',
    reset: 'Torna ai fatti',
    takeNote:
        'I fatti sono stati ottenuti con un take di {hist}. Un altro take è una stima: meno operazioni, ma ognuna più grande.',
    pts: 'pt',
    takePrice: 'Take profit, movimento del prezzo',
    robotDay: 'Il robot al giorno',
    yourShare: 'Il tuo 50%',
    perDay: 'al giorno',
    perMonth: 'al mese',
    perYear: 'all\'anno',
    yearPct: '{pct}% del deposito all\'anno',
}

const hy: FactsTranslations = {
    factsTitle: 'Ինչպես է ռոբոտն առևտուր արել',
    period: '{from} — {to} · {days} առևտրային օր · հաշիվներ՝ {accounts}',
    earned: 'Վաստակված',
    earnedHint: 'զուտ՝ բորսայի միջնորդավճարներից հետո, մինչև 50/50 բաժանումը',
    deals: 'Գործարքներ',
    volumeGlobal: 'Պայմանագրեր',
    volumeCrypto: 'Ծավալ, {coin}',
    perDayTitle: 'Միջինը օրական',
    perLot: '1 մուտքային լոտի համար',
    perContract: 'մեկ պայմանագրից',
    perTrade: 'մեկ գործարքից',
    byDays: 'Ըստ օրերի',
    dealsShort: 'գործ.',
    contractsShort: 'պայմ.',
    sharpe: 'Շարպ',
    sharpeHint: '1-ից բարձր՝ լավ, 3-ից բարձր՝ գերազանց',
    maxDrawdown: 'Ամենամեծ անկում',
    drawdownHint: 'որքան է հաշիվը ժամանակավորապես նվազել',
    sortino: 'Սորտինո',
    sortinoHint: 'ինչպես Շարպը, բայց հաշվում է միայն անկումները',
    metricsTitle: 'Ռիսկի և եկամտաբերության ցուցանիշներ',
    equityDrawdown: 'Բաց դիրքի անկում',
    equityHint: 'ըստ կապիտալի պատկերների (մնացորդ + լողացող արդյունք), օրեր՝ {days}. մնացած ցուցանիշները՝ միայն փակված գործարքներով',
    viewOwn: 'Իմ հաշիվը',
    viewRobot: 'Ռոբոտն ընդհանուր',
    ownEmpty: 'Ձեր հաշվով այս գործիքով գործարքներ դեռ չկան',
    ownInstrument: 'Ձեր հաշիվը',
    referenceNote:
        '{symbol}-ով գործարքներ դեռ չկան։ Որպես օրինակ՝ {ref}-ի փաստերը. աջ կողմի հաշվարկը՝ {symbol}-ի թեյքով, օրական գործարքները՝ ինչպես {ref}-ում։',
    metricsBasis: '{days} առևտրային օր · {from} — {to}',
    metricsDeals: 'ըստ փակված գործարքների',
    metricsFew:
        'Առայժմ 2 առևտրային օրից քիչ է․ ցուցանիշները կհայտնվեն երկրորդ օրից։',
    calmar: 'Կալմար',
    calmarHint: 'տարեկան եկամտաբերություն / առավելագույն անկում',
    volatility: 'Տատանողականություն, տարեկան',
    periodReturn: 'Եկամտաբերություն ժամանակահատվածում',
    profitFactor: 'Պրոֆիտ-ֆակտոր',
    pfHint: 'համախառն շահույթ / համախառն վնաս',
    winRate: 'Շահույթով գործարքներ',
    winDays: 'Շահույթով օրեր',
    bestDay: 'Լավագույն օր',
    worstDay: 'Վատագույն օր',
    noLosses: 'վնասներ չեն եղել',
    checkTitle: 'Ստուգեք ինքներդ',
    checkHint: 'Շարժեք սահիչները։ Սկզբում դրանք ռոբոտի փաստերի վրա են։',
    deposit: 'Ավանդ',
    lot: 'Մուտքային լոտ',
    take: 'Թեյք-պրոֆիտ, կետ',
    contractsDay: 'Պայմանագիր օրական 1 լոտի համար',
    tradesDay: 'Գործարք օրական',
    fact: 'փաստ՝ {v}',
    reset: 'Վերադարձնել փաստերը',
    takeNote:
        'Փաստերը հավաքվել են {hist} թեյքով։ Այլ թեյքը հաշվարկ է՝ գործարքները քիչ են, բայց յուրաքանչյուրը՝ մեծ։',
    pts: 'կետ',
    takePrice: 'Թեյք-պրոֆիտ, գնի շարժ',
    robotDay: 'Ռոբոտը օրական',
    yourShare: 'Ձեր 50%-ը',
    perDay: 'օրական',
    perMonth: 'ամսական',
    perYear: 'տարեկան',
    yearPct: 'ավանդի {pct}%-ը տարեկան',
}

const factsTranslations: Record<string, FactsTranslations> = {
    ru,
    en,
    es,
    fr,
    de,
    it,
    hy,
}

export default factsTranslations
